// Ported from design/project/App20-SharePreview.dc.html (Chat card) and App20b-ShareStory.dc.html (Story), then
// wired: the preview is the real card the server drew, and each button sends it where it says.
import { useEffect, useState } from "react";
import { fit } from "../fit.js";
import { go } from "../router.js";
import { makeShare, saveImage, shareContext, shareToStory, shareToTelegram, shareToWhatsApp, shareToX, type Share } from "../share.js";
import { tg } from "../telegram.js";

const TG = <svg width="24" height="24" viewBox="0 0 24 24" style={{ display: "block" }} aria-hidden="true"><path d="M21.5 3.6 2.9 10.8c-1.2.5-1.2 1.2-.2 1.5l4.8 1.5 1.8 5.6c.2.6.4.8.9.8.4 0 .6-.2.9-.5l2.3-2.2 4.7 3.5c.9.5 1.5.2 1.7-.8l3.1-14.6c.3-1.3-.5-1.9-1.4-1.5Z" fill="#FFFFFF"></path><path d="m9.3 13.8 8.9-5.6c.4-.3.8-.1.5.2l-7.4 6.7-.3 3.4" fill="#C8DAEA"></path></svg>;
const WA = <svg width="26" height="26" viewBox="0 0 24 24" style={{ display: "block" }} aria-hidden="true"><path d="M12 2.2a9.7 9.7 0 0 0-8.4 14.6L2.3 21.7l5-1.3A9.7 9.7 0 1 0 12 2.2Z" fill="#FFFFFF"></path><path d="M8.6 7.2c-.2-.5-.5-.5-.7-.5h-.6c-.2 0-.6.1-.9.4-.3.3-1.2 1.2-1.2 2.9s1.2 3.4 1.4 3.6c.2.2 2.4 3.8 5.9 5.2 2.9 1.1 3.5.9 4.1.9.6-.1 2-.8 2.3-1.6.3-.8.3-1.5.2-1.6-.1-.1-.3-.2-.7-.4l-2.3-1.1c-.3-.1-.5-.2-.8.2l-1 1.3c-.2.2-.4.3-.7.1-.4-.2-1.5-.6-2.8-1.8-1-.9-1.7-2-1.9-2.4-.2-.4 0-.6.2-.7l.5-.6c.2-.2.2-.4.4-.6.1-.2.1-.5 0-.7l-1.2-2.6Z" fill="#25D366"></path></svg>;
const XX = <svg width="22" height="22" viewBox="0 0 24 24" style={{ display: "block" }} aria-hidden="true"><path d="M17.8 3h3.1l-6.8 7.7L22 21h-6.2l-4.9-6.4L5.3 21H2.2l7.2-8.3L1.8 3h6.4l4.4 5.8L17.8 3Zm-1.1 16.2h1.7L7.3 4.7H5.5l11.2 14.5Z" fill="#FFFFFF"></path></svg>;

function Target({ name, bg, icon, onClick, disabled }: { name: string; bg: string; icon: JSX.Element; onClick: () => void; disabled: boolean }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ flex: "1", display: "flex", flexDirection: "column", alignItems: "center", gap: "6px", background: "none", border: "0", cursor: "pointer", color: "#FFFFFF", fontFamily: "Inter, sans-serif", fontSize: "12px", fontWeight: "600", opacity: disabled ? 0.5 : 1, padding: "0" }}>
      <span style={{ width: fit(44, 50), height: fit(44, 50), borderRadius: "16px", background: bg, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 20px rgba(0,0,0,0.35)", border: bg === "#000000" ? "1px solid rgba(255,255,255,0.25)" : "0" }}>{icon}</span>
      {name}
    </button>
  );
}

export function App20_SharePreview() {
  const ctx = shareContext();
  const [tab, setTab] = useState<"chat" | "story">("chat");
  const [showAmount, setShowAmount] = useState(true);
  const [share, setShare] = useState<Share | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // "Drawing your card" only if it genuinely isn't ready after a moment; normally the card is already there.
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    setSlow(false);
    const t = setTimeout(() => setSlow(true), 600);
    return () => clearTimeout(t);
  }, [showAmount, tab]);
  const [caption, setCaption] = useState(() =>
    ctx?.won ? `Called it. Who's next? Beat me on ${ctx.arena} for $${ctx.stake.replace(/\.0+$/, "")}.` : `Took an L on ${ctx?.arena ?? "BTC"}. Think you can do better?`);
  const [storyCaption, setStoryCaption] = useState(ctx?.won ? "Called it. Who's next?" : "Roasted. Run it back?");

  useEffect(() => {
    if (!ctx) return go("App09", true);
    let gone = false;
    setShare(null);
    setLoaded(false);
    makeShare(ctx.battleId, showAmount)
      .then((s) => {
        if (!gone) setShare(s);
        // The other Show amount choice gets drawn too, so flipping the switch is instant.
        void makeShare(ctx.battleId, !showAmount).catch(() => {});
      })
      .catch(() => !gone && setProblem("The card couldn't be made. Check your connection and try again."));
    return () => {
      gone = true;
    };
  }, [showAmount]);
  if (!ctx) return null;

  const ready = !!share;
  const img = share ? (tab === "chat" ? share.image : share.story) : null;
  const copy = async () => {
    if (!share) return;
    await navigator.clipboard?.writeText(share.play).catch(() => {});
    tg?.HapticFeedback?.notificationOccurred("success");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const story = () => {
    if (share && !shareToStory(share, storyCaption)) setProblem("Posting stories needs a newer Telegram. Save the image and post it instead.");
  };
  const fileName = share ? `degenduel-${share.id}${tab === "story" ? "-story" : ""}.png` : "";

  const accent = ctx.won ? "#CCFF00" : "#FF3366";
  return (
    <>
      <div className="screen">
  <div className="orbL" aria-hidden="true" style={{ top: "60px", left: "-45px", width: "480px", height: "480px" }}></div>
  <div className="pad squeeze" style={{ height: "100%", paddingBottom: "calc(var(--dd-nav-bottom) + 4px)", display: "flex", flexDirection: "column" }}>
    <div className="top" style={{ justifyContent: "center" }}>
      <span className="syne" style={{ fontWeight: "800", fontSize: "17px" }}>Share</span>
    </div>

    <div className="tabs g" style={{ marginTop: fit(4, 12), gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
      <button className={tab === "chat" ? "tab on" : "tab"} onClick={() => setTab("chat")}>Chat card</button>
      <button className={tab === "story" ? "tab on" : "tab"} onClick={() => setTab("story")}>Story</button>
    </div>

    {/* The real card the server drew. Short phones shrink it first, so the buttons below always stay on screen. */}
    <div style={{ marginTop: fit(10, 14), alignSelf: "center", position: "relative",
      width: tab === "chat" ? fit(140, 212) : fit(96, 168), aspectRatio: tab === "chat" ? "1 / 1" : "9 / 16",
      borderRadius: "20px", overflow: "hidden", border: "3px solid #FFFFFF", boxShadow: `8px 8px 0 ${accent}, 0 20px 50px rgba(0,0,0,0.5)`, background: ctx.won ? "#141A08" : "#1D0A10" }}>
      {img && <img src={img} alt={ctx.won ? "Your victory card" : "Your roast card"} onLoad={() => setLoaded(true)} style={{ width: "100%", height: "100%", display: "block", opacity: loaded ? 1 : 0, transition: "opacity 0.2s" }} />}
      {!loaded && slow && <span className="muted" style={{ position: "absolute", inset: "0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px" }}>Drawing your card...</span>}
    </div>

    <div className="g" style={{ marginTop: fit(12, 16), borderRadius: "14px", padding: fit(8, 10) + " 12px", display: "flex", alignItems: "center", gap: "10px" }}>
      <span style={{ flex: "1", display: "flex", flexDirection: "column", gap: "2px" }}><span style={{ fontSize: "14px", fontWeight: "600" }}>Show amount</span><span className="muted tallOnly" style={{ fontSize: "11px" }}>Off hides the USDT amount on the card</span></span>
      <button role="switch" aria-checked={showAmount} aria-label="Show amount" onClick={() => setShowAmount((v) => !v)}
        style={{ flex: "none", padding: "10px 4px", margin: "-10px -4px", border: "0", background: "none", cursor: "pointer" }}>
        <span style={{ display: "block", width: "46px", height: "28px", borderRadius: "14px", background: showAmount ? "#CCFF00" : "#30363D", position: "relative" }}>
          <span style={{ position: "absolute", top: "3px", left: showAmount ? "21px" : "3px", width: "22px", height: "22px", borderRadius: "50%", background: showAmount ? "#0B0E14" : "#8B949E", transition: "left 0.15s" }}></span>
        </span>
      </button>
    </div>

    <label htmlFor="spCap" className="lbl" style={{ marginTop: fit(10, 14) }}>{tab === "chat" ? "CAPTION" : "CAPTION ON THE STORY"}</label>
    <textarea id="spCap" rows={tab === "chat" ? 2 : 1} maxLength={tab === "chat" ? 300 : 200} value={tab === "chat" ? caption : storyCaption} onChange={(e) => (tab === "chat" ? setCaption : setStoryCaption)(e.target.value)}
      style={{ flex: "none", marginTop: "8px", resize: "none", padding: fit(9, 12) + " 14px", borderRadius: "14px", background: "#0B0E14", border: "1px solid #30363D", color: "#FFFFFF", fontFamily: "Inter, sans-serif", fontSize: "14px", lineHeight: "20px", outline: "none" }} />
    {tab === "chat" && (
      <div className="g" style={{ marginTop: "10px", borderRadius: "14px", padding: fit(8, 10) + " 12px", display: "flex", alignItems: "center", gap: "10px" }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#CCFF00" strokeWidth="2" strokeLinecap="round" style={{ display: "block", flex: "none" }} aria-hidden="true"><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1"></path><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1"></path></svg>
        <span className="mono" style={{ flex: "1", minWidth: "0", fontSize: "12px", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{share ? share.play.replace("https://", "") : "..."}</span>
        <button onClick={() => void copy()} disabled={!ready} className="lime" style={{ fontSize: "12px", fontWeight: "600", background: "none", border: "0", padding: "14px 10px", margin: "-14px -10px", cursor: "pointer", fontFamily: "inherit" }}>{copied ? "Copied" : "Copy"}</button>
      </div>
    )}

    {problem && <span role="alert" style={{ marginTop: "8px", color: "#FF5C85", fontSize: "12px", lineHeight: "17px", textAlign: "center" }}>{problem}</span>}

    <div style={{ marginTop: "auto", paddingTop: fit(14, 20), display: "flex", flexDirection: "column", gap: fit(8, 12) }}>
      {tab === "chat" ? (
        <>
          <span className="lbl" style={{ textAlign: "center" }}>SHARE TO</span>
          <div style={{ display: "flex", justifyContent: "space-around" }}>
            <Target name="Telegram" bg="#2AABEE" icon={TG} disabled={!ready} onClick={() => share && void shareToTelegram(share, caption)} />
            <Target name="WhatsApp" bg="#25D366" icon={WA} disabled={!ready} onClick={() => share && shareToWhatsApp(share, caption)} />
            <Target name="X" bg="#000000" icon={XX} disabled={!ready} onClick={() => share && shareToX(share, caption)} />
          </div>
          <button className="btnS" disabled={!ready} onClick={() => share && saveImage(share.image, fileName)} style={{ cursor: "pointer" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B949E" strokeWidth="2" strokeLinecap="round" style={{ display: "block" }} aria-hidden="true"><path d="M12 4v11M7 10l5 5 5-5M5 20h14"></path></svg>
            Save image
          </button>
        </>
      ) : (
        <>
          <button className="btnP" disabled={!ready} onClick={story} style={{ border: "0", cursor: "pointer" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0B0E14" strokeWidth="2.4" strokeLinecap="round" style={{ display: "block" }} aria-hidden="true"><circle cx="12" cy="12" r="9" strokeDasharray="4 2.5"></circle><path d="M12 8v8M8 12h8"></path></svg>
            POST TO MY STORY
          </button>
          <button className="btnS" disabled={!ready} onClick={() => share && saveImage(share.story, fileName)} style={{ cursor: "pointer", whiteSpace: "normal", textAlign: "center", lineHeight: "18px" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B949E" strokeWidth="2" strokeLinecap="round" style={{ display: "block", flex: "none" }} aria-hidden="true"><path d="M12 4v11M7 10l5 5 5-5M5 20h14"></path></svg>
            Save image for WhatsApp Status or Instagram
          </button>
        </>
      )}
    </div>
  </div>
</div>
    </>
  );
}
